import { Link } from 'react-router-dom';
import { Shield, Database, Zap, CreditCard, Mail, PlaneTakeoff, BarChart3, Lock, Server, Globe, Github, Tags, Repeat } from 'lucide-react';
import { motion } from 'framer-motion';
import { Logo } from '@/components/shared/Logo';

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: (i: number) => ({ opacity: 1, y: 0, transition: { delay: i * 0.1, duration: 0.5 } }),
};

export function LandingPage() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <Nav />
      <Hero />
      <Features />
      <HowItWorks />
      <SelfHosted />
      <Comparison />
      <CTA />
      <Footer />
    </div>
  );
}

function Nav() {
  return (
    <nav className="sticky top-0 z-50 border-b border-border/50 bg-background/80 backdrop-blur-lg">
      <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
        <Logo size="md" linkTo="/" />
        <div className="hidden items-center gap-8 md:flex">
          <a href="#features" className="text-sm text-muted-foreground transition-colors hover:text-foreground">Features</a>
          <a href="#how-it-works" className="text-sm text-muted-foreground transition-colors hover:text-foreground">How It Works</a>
          <a href="#self-hosted" className="text-sm text-muted-foreground transition-colors hover:text-foreground">Self-Hosted</a>
          <a href="https://github.com/snvishna/tripper" target="_blank" rel="noopener noreferrer" className="text-sm text-muted-foreground transition-colors hover:text-foreground">
            <Github className="h-4 w-4" />
          </a>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/login" className="text-sm font-medium text-muted-foreground transition-colors hover:text-foreground">
            Sign in
          </Link>
          <Link to="/login" className="rounded-xl bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90">
            Get Started
          </Link>
        </div>
      </div>
    </nav>
  );
}

function Hero() {
  return (
    <section className="relative overflow-hidden px-6 py-24 md:py-32">
      <div className="absolute inset-0 -z-10 bg-[radial-gradient(ellipse_at_top,hsl(var(--primary)/0.08),transparent_60%)]" />
      <div className="mx-auto max-w-4xl text-center">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <span className="inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/5 px-4 py-1.5 text-xs font-medium text-primary">
            <Tags className="h-3.5 w-3.5" />
            Every expense, tagged and totalled
          </span>
        </motion.div>
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="mt-6 text-4xl font-bold tracking-tight md:text-6xl lg:text-7xl"
        >
          Know where every dollar goes
          <span className="block text-primary">without lifting a finger</span>
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mx-auto mt-6 max-w-2xl text-lg text-muted-foreground md:text-xl"
        >
          Tally reads the receipts in your inbox and turns them into one clean list of expenses —
          tagged by trip, subscription and whatever else matters to you. Self-hosted, so your data stays yours.
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row"
        >
          <Link to="/login" className="w-full rounded-xl bg-primary px-8 py-3.5 text-sm font-medium text-primary-foreground shadow-lg shadow-primary/20 transition-all hover:bg-primary/90 hover:shadow-xl hover:shadow-primary/30 sm:w-auto">
            Start tracking for free
          </Link>
          <a href="#how-it-works" className="w-full rounded-xl border border-border bg-card px-8 py-3.5 text-sm font-medium shadow-sm transition-all hover:bg-accent sm:w-auto">
            See how it works
          </a>
        </motion.div>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="mt-4 text-xs text-muted-foreground"
        >
          Free forever. Self-hosted on your own infrastructure.
        </motion.p>
      </div>
    </section>
  );
}

function Features() {
  const features = [
    { icon: Tags, title: 'Tags That Fill Themselves', description: 'AI groups merchants into tags like Streaming, AI Tools or Groceries; you can add, move and rename anything.' },
    { icon: Repeat, title: 'Subscriptions Found', description: 'Recurring charges are spotted automatically, with what each costs per month, per year and since you started.' },
    { icon: PlaneTakeoff, title: 'Trips Detected', description: 'Flights, hotels and spending away from home are grouped into trips — just another tag on your expenses.' },
    { icon: CreditCard, title: 'No Double Counting', description: 'Booking confirmations, reminders, and card alerts for the same purchase are merged into one expense.' },
    { icon: Mail, title: 'Email Receipt Scanning', description: 'Gmail integration pulls receipts, boarding passes, and booking confirmations.' },
    { icon: BarChart3, title: 'Answers, Not Spreadsheets', description: 'How much last quarter, last year or ever; per merchant, tag or category — in interactive charts.' },
    { icon: Lock, title: 'AES-256 Encryption', description: 'All credentials and tokens encrypted at rest. OAuth tokens never exposed to the browser.' },
    { icon: Server, title: '100% Self-Hosted', description: 'Deploy on your own cloud. No third party ever sees your financial data.' },
  ];

  return (
    <section id="features" className="border-t border-border/50 px-6 py-24">
      <div className="mx-auto max-w-6xl">
        <div className="text-center">
          <h2 className="text-3xl font-bold md:text-4xl">Everything you need to understand your spending</h2>
          <p className="mx-auto mt-4 max-w-2xl text-muted-foreground">
            No manual entry. No spreadsheets. Connect your inbox and Tally handles the rest.
          </p>
        </div>
        <div className="mt-16 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
          {features.map((f, i) => (
            <motion.div
              key={f.title}
              custom={i}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: '-50px' }}
              variants={fadeUp}
              className="rounded-2xl border border-border/50 bg-card p-6 transition-shadow hover:shadow-lg"
            >
              <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10">
                <f.icon className="h-5 w-5 text-primary" />
              </div>
              <h3 className="text-lg font-semibold">{f.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{f.description}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function HowItWorks() {
  const steps = [
    { step: '1', title: 'Connect your inbox', description: 'Sign in with Google and Tally reads your receipts, bookings and card alerts — once, with duplicates merged.' },
    { step: '2', title: 'Every expense, tagged', description: 'Trips, subscriptions and topics like Streaming or Groceries are tagged automatically. Move or retag anything in bulk.' },
    { step: '3', title: 'Ask anything', description: 'How much on OneDrive since 2022? Last quarter vs last year? Insights answer with totals and charts.' },
    { step: '4', title: 'Export and report', description: 'CSV exports by tag, trip or date range — for reimbursement, taxes or your own spreadsheets.' },
  ];

  return (
    <section id="how-it-works" className="border-t border-border/50 bg-muted/30 px-6 py-24">
      <div className="mx-auto max-w-4xl">
        <div className="text-center">
          <h2 className="text-3xl font-bold md:text-4xl">How it works</h2>
          <p className="mt-4 text-muted-foreground">Four steps to automated expense tracking</p>
        </div>
        <div className="mt-16 space-y-12">
          {steps.map((s, i) => (
            <motion.div
              key={s.step}
              custom={i}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: '-50px' }}
              variants={fadeUp}
              className="flex gap-6"
            >
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-bold text-primary-foreground">
                {s.step}
              </div>
              <div>
                <h3 className="text-lg font-semibold">{s.title}</h3>
                <p className="mt-1 text-muted-foreground">{s.description}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function SelfHosted() {
  return (
    <section id="self-hosted" className="border-t border-border/50 px-6 py-24">
      <div className="mx-auto max-w-4xl">
        <div className="rounded-3xl border border-border bg-gradient-to-br from-primary/5 via-transparent to-primary/5 p-8 md:p-12">
          <div className="flex flex-col items-center gap-8 md:flex-row">
            <div className="flex-1 space-y-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10">
                <Database className="h-6 w-6 text-primary" />
              </div>
              <h2 className="text-2xl font-bold md:text-3xl">Your data. Your server. Period.</h2>
              <p className="text-muted-foreground">
                Tally is designed to run on your own infrastructure. Deploy to Oracle Cloud Free Tier,
                a home server, or any Docker-compatible host. We never see your financial data.
              </p>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-center gap-2"><Shield className="h-4 w-4 text-primary" /> No vendor lock-in</li>
                <li className="flex items-center gap-2"><Shield className="h-4 w-4 text-primary" /> Oracle Cloud Always-Free compatible</li>
                <li className="flex items-center gap-2"><Shield className="h-4 w-4 text-primary" /> Docker one-command deploy</li>
                <li className="flex items-center gap-2"><Shield className="h-4 w-4 text-primary" /> Full source code available</li>
              </ul>
            </div>
            <div className="w-full flex-1 overflow-hidden rounded-xl border border-border bg-background p-4 font-mono text-xs leading-relaxed md:w-auto">
              <div className="flex gap-1.5 pb-3">
                <div className="h-3 w-3 rounded-full bg-red-400" />
                <div className="h-3 w-3 rounded-full bg-yellow-400" />
                <div className="h-3 w-3 rounded-full bg-green-400" />
              </div>
              <code className="text-muted-foreground">
                <span className="text-primary">$</span> git clone tripper<br/>
                <span className="text-primary">$</span> cp .env.example .env<br/>
                <span className="text-primary">$</span> docker-compose up<br/>
                <br/>
                <span className="text-green-500">✓</span> Oracle DB connected<br/>
                <span className="text-green-500">✓</span> API on :3001<br/>
                <span className="text-green-500">✓</span> Tally running!
              </code>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Comparison() {
  const rows = [
    { feature: 'Monthly cost', tally: 'Free ($0)', others: '$5–15/month' },
    { feature: 'Your data stays private', tally: 'Always', others: 'Shared with vendor' },
    { feature: 'Automatic tags & subscriptions', tally: 'Yes', others: 'Manual entry' },
    { feature: 'Trips detected for you', tally: 'Yes', others: 'No' },
    { feature: 'Reads your email receipts', tally: 'Yes', others: 'Partial' },
    { feature: 'Open source', tally: 'Yes', others: 'No' },
    { feature: 'Self-hosted option', tally: 'Yes', others: 'No' },
  ];

  return (
    <section className="border-t border-border/50 px-6 py-24">
      <div className="mx-auto max-w-3xl">
        <div className="text-center">
          <h2 className="text-3xl font-bold md:text-4xl">Why Tally?</h2>
          <p className="mt-4 text-muted-foreground">Compared to paid expense trackers and spreadsheets</p>
        </div>
        <div className="mt-12 overflow-hidden rounded-2xl border border-border">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/50">
                <th className="px-6 py-4 text-left font-medium text-muted-foreground">Feature</th>
                <th className="px-6 py-4 text-center font-semibold text-primary">Tally</th>
                <th className="px-6 py-4 text-center font-medium text-muted-foreground">Others</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.feature} className="border-b border-border/50 last:border-0">
                  <td className="px-6 py-3.5">{row.feature}</td>
                  <td className="px-6 py-3.5 text-center font-medium text-primary">{row.tally}</td>
                  <td className="px-6 py-3.5 text-center text-muted-foreground">{row.others}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

function CTA() {
  return (
    <section className="border-t border-border/50 bg-gradient-to-b from-transparent to-primary/5 px-6 py-24">
      <div className="mx-auto max-w-2xl text-center">
        <h2 className="text-3xl font-bold md:text-4xl">Ready to stop tracking expenses manually?</h2>
        <p className="mt-4 text-lg text-muted-foreground">
          Deploy Tally in minutes and never lose another receipt.
        </p>
        <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
          <Link to="/login" className="w-full rounded-xl bg-primary px-8 py-3.5 text-sm font-medium text-primary-foreground shadow-lg shadow-primary/20 transition-all hover:bg-primary/90 sm:w-auto">
            Get Started
          </Link>
          <a href="https://github.com/snvishna/tripper" target="_blank" rel="noopener noreferrer" className="flex w-full items-center justify-center gap-2 rounded-xl border border-border bg-card px-8 py-3.5 text-sm font-medium shadow-sm transition-all hover:bg-accent sm:w-auto">
            <Github className="h-4 w-4" /> View on GitHub
          </a>
        </div>
        <p className="mt-4 text-xs text-muted-foreground">
          Open source. MIT licensed. Deploy on your own infrastructure.
        </p>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="border-t border-border/50 px-6 py-12">
      <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-6 md:flex-row">
        <Logo size="sm" linkTo="/" />
        <div className="flex items-center gap-6 text-sm text-muted-foreground">
          <a href="https://github.com/snvishna/tripper" target="_blank" rel="noopener noreferrer" className="hover:text-foreground">GitHub</a>
          <a href="https://github.com/snvishna/tripper/blob/main/docs/DEPLOYMENT.md" target="_blank" rel="noopener noreferrer" className="hover:text-foreground">Deploy Guide</a>
          <a href="https://github.com/snvishna/tripper/blob/main/LICENSE" target="_blank" rel="noopener noreferrer" className="hover:text-foreground">MIT License</a>
        </div>
      </div>
    </footer>
  );
}
